import { ContentFilterQueryParamKey } from '@as-react/common-types';
import { isMultiSelectFilter, isRangeFilter, isBucketFilter, isNullOrUndefined } from '@as-react/common-utils';
/**
 * Generate URL filter value for multiselect filters
 * Format: (value1;value2) for OR, (value1,value2) for AND (when displayHint is 'multiselect-and')
 */
const generateMultiSelectFilterValue = (filteredItems, filterDisplayHint) => {
    const useCommaSeparator = !isNullOrUndefined(filterDisplayHint) && filterDisplayHint.length > 0 && filterDisplayHint[0] === 'multiselect-and';
    const separator = useCommaSeparator ? ',' : ';';
    return `(${filteredItems.map((item) => item.filterKey).join(separator)})`;
};
/**
 * Generate URL filter value for range filters
 * Format: [min;max]
 */
const generateRangeFilterValue = (filteredItems) => {
    return `[${filteredItems.map((item) => item.filterKey).join(';')}]`;
};
/**
 * Generate URL filter value for bucket filters
 * Format: direct value (last item's filterKey)
 */
const generateBucketFilterValue = (filteredItems) => {
    var _a;
    const lastItem = filteredItems[filteredItems.length - 1];
    return (_a = lastItem === null || lastItem === void 0 ? void 0 : lastItem.filterKey) !== null && _a !== void 0 ? _a : '';
};
/**
 * Remove a filter from the URL filter array by filterOn
 */
const removeUrlFilter = (initialFilter, filterOn) => {
    if (!initialFilter) {
        return [];
    }
    return initialFilter.filter((urlFilterObject) => urlFilterObject.filterOn !== filterOn);
};
/**
 * Set/update a filter in the URL filter array
 */
const setUrlFilter = (currentFilters, filterOn, filterValue) => {
    const filtersWithoutCurrent = removeUrlFilter(currentFilters, filterOn);
    return [
        ...filtersWithoutCurrent,
        {
            filterOn,
            filterValue,
        },
    ];
};
/**
 * Generate filter value based on filter type
 */
const generateFilterValue = (setFilterObject) => {
    if (isMultiSelectFilter(setFilterObject)) {
        return generateMultiSelectFilterValue(setFilterObject.filteredItems, setFilterObject.filterDisplayHint);
    }
    if (isRangeFilter(setFilterObject)) {
        return generateRangeFilterValue(setFilterObject.filteredItems);
    }
    if (isBucketFilter(setFilterObject)) {
        return generateBucketFilterValue(setFilterObject.filteredItems);
    }
    // Default: use multiselect format
    return generateMultiSelectFilterValue(setFilterObject.filteredItems, setFilterObject.filterDisplayHint);
};
/**
 * Apply a filter to the URL state
 * Returns the updated URL state
 */
export const applyFilterToUrlState = (urlState, setFilterObject) => {
    // Content type filters use special handling
    if (setFilterObject.queryParamKey === ContentFilterQueryParamKey.TYPE) {
        return Object.assign(Object.assign({}, urlState), { page: 0 });
    }
    if (setFilterObject.queryParamKey === ContentFilterQueryParamKey.CATEGORY) {
        return Object.assign(Object.assign({}, urlState), { page: 0 });
    }
    // Product filters
    const filterValue = generateFilterValue(setFilterObject);
    return Object.assign(Object.assign({}, urlState), { filter: setUrlFilter(urlState.filter, setFilterObject.filterOn, filterValue), page: 0 });
};
/**
 * Remove a filter from the URL state
 */
export const removeFilterFromUrlState = (urlState, filterOn, queryParamKey) => {
    if (queryParamKey === ContentFilterQueryParamKey.TYPE) {
        return Object.assign(Object.assign({}, urlState), { page: 0 });
    }
    if (queryParamKey === ContentFilterQueryParamKey.CATEGORY) {
        return Object.assign(Object.assign({}, urlState), { page: 0 });
    }
    return Object.assign(Object.assign({}, urlState), { filter: removeUrlFilter(urlState.filter, filterOn), page: 0 });
};
/**
 * Remove all filters from the URL state
 */
export const removeAllFiltersFromUrlState = (urlState) => {
    return Object.assign(Object.assign({}, urlState), { filter: [], page: 0, storeId: undefined });
};
/**
 * Serialize URL state to URL search params string
 * Based on getURLParams from RouterSagas.ts
 */
export const serializeUrlState = (urlState, defaults) => {
    var _a;
    if (typeof window === 'undefined') {
        return '';
    }
    const params = new URLSearchParams(window.location.search);
    const defaultValues = {
        page: (_a = defaults === null || defaults === void 0 ? void 0 : defaults.page) !== null && _a !== void 0 ? _a : 0,
        sort: defaults === null || defaults === void 0 ? void 0 : defaults.sort,
    };
    Object.keys(urlState).forEach(urlStateKey => {
        const value = urlState[urlStateKey];
        if (!isNullOrUndefined(value)) {
            switch (urlStateKey) {
                case 'filter':
                    if (Array.isArray(value) && value.length > 0) {
                        const filterArray = value;
                        const flattenedFilterValue = filterArray.reduce((accumulator, currentValue) => (accumulator += `&filter=${currentValue.filterOn}:${currentValue.filterValue}`), '');
                        params.set('filter', flattenedFilterValue);
                    }
                    else {
                        params.delete(urlStateKey);
                    }
                    break;
                case 'defaultFilter':
                    // Don't include defaultFilter in URL params
                    break;
                default:
                    if (defaultValues[urlStateKey] === value) {
                        params.delete(urlStateKey);
                    }
                    else {
                        params.set(urlStateKey, String(value));
                    }
                    break;
            }
        }
        else {
            params.delete(urlStateKey);
        }
    });
    // Necessary encodeURI: prevents weird flicker issues with urls and encoded filter params
    return encodeURI(params.toString().replace(/\+/g, ' '));
};
/**
 * Parse a filter string from URL into UrlFilterObject array
 * Format: filter=brand:(nike;adidas)&filter=price:[100;500]
 */
const parseFiltersFromUrl = (searchParams) => {
    const filterParam = searchParams.get('filter');
    if (!filterParam) {
        return [];
    }
    const filters = [];
    // Handle multiple filter params (they're concatenated with &filter=)
    const filterParts = filterParam.split('&filter=').filter(Boolean);
    filterParts.forEach(part => {
        // Match format: filterOn:filterValue
        const colonIndex = part.indexOf(':');
        if (colonIndex > 0) {
            const filterOn = part.substring(0, colonIndex);
            const filterValue = part.substring(colonIndex + 1);
            filters.push({ filterOn, filterValue });
        }
    });
    return filters;
};
const normalizeQuery = (q) => (q === null || q === void 0 ? void 0 : q.replace(/\+/g, ' ')) || undefined;
/**
 * Parse URL search params to ListerUrlState
 */
export const parseUrlState = (searchParams, defaults) => {
    const page = searchParams.get('page');
    const sort = searchParams.get('sort');
    const q = searchParams.get('q');
    const storeId = searchParams.get('storeId');
    const defaultFilter = searchParams.get('defaultFilter');
    return {
        q: normalizeQuery(q !== null && q !== void 0 ? q : undefined),
        defaultFilter: defaultFilter || undefined,
        filter: parseFiltersFromUrl(searchParams),
        page: page ? parseInt(page, 10) : 0,
        sort: sort || defaults.sort,
        storeId: storeId ? parseInt(storeId, 10) : undefined,
    };
};
/**
 * Get initial URL state from window.location or defaults
 */
export const getInitialUrlState = (defaults, initialOverrides) => {
    const normalizedOverrides = initialOverrides
        ? Object.assign(Object.assign({}, initialOverrides), { q: normalizeQuery(initialOverrides.q) }) : undefined;
    if (typeof window === 'undefined') {
        return Object.assign({ filter: [], page: 0, sort: defaults.sort }, normalizedOverrides);
    }
    const searchParams = new URLSearchParams(window.location.search);
    const parsedState = parseUrlState(searchParams, defaults);
    return Object.assign(Object.assign({}, parsedState), normalizedOverrides);
};
